import React, { useState, useMemo } from 'react'
import { ProvinceSelection, CommuneSelection } from '../../types/map.types'
import './ProvinceListView.styles.css'

interface ProvinceListViewProps {
  selectedProvinces: ProvinceSelection
  selectedCommunes: CommuneSelection
  onProvinceChange: (provinceName: string, selected: boolean, communes?: Record<string, boolean>) => void
  searchQuery: string
}

const PROVINCES_DATA = {
  'Wallonie': [
    { name: 'Hainaut', communes: ['Mons', 'Charleroi', 'Tournai', 'La Louvière', 'Mouscron'] },
    { name: 'Namur', communes: ['Namur', 'Dinant', 'Ciney', 'Gembloux', 'Sambreville'] },
    { name: 'Liège', communes: ['Liège', 'Seraing', 'Verviers', 'Herstal', 'Ans'] },
    { name: 'Luxembourg', communes: ['Arlon', 'Bastogne', 'Marche-en-Famenne', 'Virton', 'Durbuy'] },
    { name: 'Brabant Wallon', communes: ['Wavre', 'Nivelles', 'Ottignies-Louvain-la-Neuve', 'Braine-l\'Alleud', 'Waterloo'] }
  ],
  'Flandre': [
    { name: 'Anvers', communes: ['Anvers', 'Malines', 'Turnhout', 'Herentals', 'Lierre'] },
    { name: 'Limbourg', communes: ['Hasselt', 'Genk', 'Sint-Truiden', 'Tongeren', 'Lommel'] },
    { name: 'Flandre Orientale', communes: ['Gand', 'Alost', 'Sint-Niklaas', 'Termonde', 'Eeklo'] },
    { name: 'Flandre Occidentale', communes: ['Bruges', 'Courtrai', 'Ostende', 'Roulers', 'Ypres'] },
    { name: 'Brabant Flamand', communes: ['Louvain', 'Vilvorde', 'Hal', 'Tirlemont', 'Aarschot'] }
  ],
  'Bruxelles': [
    { name: 'Bruxelles', communes: ['Bruxelles', 'Schaerbeek', 'Anderlecht', 'Ixelles', 'Uccle', 'Molenbeek-Saint-Jean', 'Forest', 'Woluwe-Saint-Lambert'] }
  ]
}

export const ProvinceListView: React.FC<ProvinceListViewProps> = ({
  selectedProvinces,
  selectedCommunes,
  onProvinceChange,
  searchQuery
}) => {
  const [expandedProvinces, setExpandedProvinces] = useState<Set<string>>(new Set())

  const filteredData = useMemo(() => {
    if (!searchQuery) return PROVINCES_DATA

    const query = searchQuery.toLowerCase()
    const filtered: typeof PROVINCES_DATA = { 'Wallonie': [], 'Flandre': [], 'Bruxelles': [] }

    Object.entries(PROVINCES_DATA).forEach(([region, provinces]) => {
      provinces.forEach(province => {
        const provinceMatches = province.name.toLowerCase().includes(query)
        const matchingCommunes = province.communes.filter(commune => 
          commune.toLowerCase().includes(query)
        )

        if (provinceMatches || matchingCommunes.length > 0) {
          filtered[region as keyof typeof PROVINCES_DATA].push({
            ...province,
            communes: provinceMatches ? province.communes : matchingCommunes
          })
        }
      })
    })

    return filtered
  }, [searchQuery])

  const toggleProvince = (provinceName: string) => {
    const newExpanded = new Set(expandedProvinces)
    if (newExpanded.has(provinceName)) {
      newExpanded.delete(provinceName)
    } else {
      newExpanded.add(provinceName)
    }
    setExpandedProvinces(newExpanded)
  }

  const handleProvinceSelect = (provinceName: string, checked: boolean) => {
    if (checked) {
      const province = Object.values(PROVINCES_DATA).flat().find(p => p.name === provinceName)
      if (province) {
        const allCommunes = province.communes.reduce((acc, commune) => {
          acc[commune] = true
          return acc
        }, {} as Record<string, boolean>)
        onProvinceChange(provinceName, true, allCommunes)
      }
    } else {
      onProvinceChange(provinceName, false, {})
    }
  }

  const handleCommuneSelect = (provinceName: string, communeName: string, checked: boolean) => {
    const currentCommunes = selectedCommunes[provinceName] || {}
    const updatedCommunes = {
      ...currentCommunes,
      [communeName]: checked
    }
    
    const hasSelectedCommunes = Object.values(updatedCommunes).some(v => v)
    onProvinceChange(provinceName, hasSelectedCommunes, updatedCommunes)
  }

  const isProvinceIndeterminate = (provinceName: string) => {
    const communes = selectedCommunes[provinceName]
    if (!communes) return false
    const values = Object.values(communes)
    return values.some(v => v) && !values.every(v => v)
  }

  return (
    <div className="province-list-view">
      {Object.entries(filteredData).map(([region, provinces]) => (
        provinces.length > 0 && (
          <div key={region} className="region-section">
            <h3 className="region-title">{region}</h3>
            <div className="provinces-list">
              {provinces.map(province => {
                const isExpanded = expandedProvinces.has(province.name)
                const isSelected = selectedProvinces[province.name]
                const isIndeterminate = isProvinceIndeterminate(province.name)
                
                return (
                  <div key={province.name} className="province-item">
                    <div className="province-header">
                      <button
                        className={`expand-btn ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleProvince(province.name)}
                        aria-label={isExpanded ? 'Réduire' : 'Développer'}
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                          <path d={isExpanded ? 'M2 4l4 4 4-4' : 'M4 2l4 4-4 4'} />
                        </svg>
                      </button>
                      
                      <label className="province-checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected || false}
                          ref={el => {
                            if (el) el.indeterminate = isIndeterminate
                          }}
                          onChange={(e) => handleProvinceSelect(province.name, e.target.checked)}
                        />
                        <span className="province-name">{province.name}</span>
                        <span className="commune-count">
                          ({selectedCommunes[province.name] ? 
                            Object.values(selectedCommunes[province.name]).filter(Boolean).length : 0
                          }/{province.communes.length})
                        </span>
                      </label>
                    </div>
                    
                    {isExpanded && (
                      <div className="communes-list">
                        {province.communes.map(commune => (
                          <label key={commune} className="commune-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedCommunes[province.name]?.[commune] || false}
                              onChange={(e) => handleCommuneSelect(province.name, commune, e.target.checked)}
                            />
                            <span className="commune-name">{commune}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      ))}
      
      {Object.values(filteredData).every(provinces => provinces.length === 0) && (
        <div className="no-results">
          <p>Aucun résultat trouvé pour "{searchQuery}"</p>
        </div>
      )}
    </div>
  )
}